import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { Check, Trash, X } from '@phosphor-icons/react';

import { ApiError } from '@/api/client';
import {
  addParticipants,
  listAddressBook,
  listParticipants,
  removeParticipant,
  renameConversation,
  type AddressBookMember,
  type Conversation,
} from '@/api/chat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn } from '@/lib/utils';

// EditChatModal is the owner-only editor for an existing conversation: rename
// + add/remove participants in one panel. Composition mirrors NewChatModal
// (same dialog shell, same address-book accordion for picking adds) so the
// two flows stay visually consistent. The participants section at the top
// shows who's already in; the picker below shows the roster minus everyone
// already in. Save fires rename + addParticipants only when the corresponding
// field actually changed.

export interface EditChatModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  conversation: Conversation | null;
  // Resolved fallback title (when conversation.title is empty) so the input
  // shows "Group chat" instead of "" — the user can clear it to mean "no
  // explicit name, use the fallback".
  title: string;
}

export function EditChatModal({ open, onClose, slug, conversation, title }: EditChatModalProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const conversationId = conversation?.id ?? null;

  const [nameDraft, setNameDraft] = useState('');
  const [search, setSearch] = useState('');
  const [toAdd, setToAdd] = useState<Set<string>>(new Set());
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Open/close the native <dialog>; reset state every time we open a new chat.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      // The current persisted title — empty when the chat has no explicit
      // title (fallback rendering is "Group chat" / "Direct message").
      setNameDraft(conversation?.title ?? '');
      setSearch('');
      setToAdd(new Set());
      setErrorCode(null);
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open, conversation?.title]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  const participantsQuery = useQuery({
    queryKey: ['chat', 'participants', slug, conversationId],
    queryFn: () => listParticipants(slug, conversationId!, token),
    enabled: !!token && !!slug && !!conversationId && open,
  });

  const addressBookQuery = useQuery({
    queryKey: ['chat', 'address-book', slug],
    queryFn: () => listAddressBook(slug, token),
    enabled: !!token && !!slug && open,
  });

  const participants = useMemo(() => participantsQuery.data ?? [], [participantsQuery.data]);
  const teams = useMemo(() => addressBookQuery.data ?? [], [addressBookQuery.data]);

  // Member ids already in the chat — used to filter the picker so we don't
  // offer to add someone who's already a participant.
  const existingMemberIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of participants) if (p.member_id) ids.add(p.member_id);
    return ids;
  }, [participants]);

  // Picker rows: flat search-filtered list across every team (minus already-in
  // members + the synthetic "no team" bucket members). Same dedup-by-member-id
  // shape NewChatModal uses for its search mode.
  const q = search.trim().toLowerCase();
  const pickerRows = useMemo(() => {
    const byId = new Map<string, AddressBookMember>();
    for (const team of teams) {
      for (const m of team.members) {
        if (byId.has(m.member_id)) continue;
        if (existingMemberIds.has(m.member_id)) continue;
        if (
          !q ||
          m.display_name.toLowerCase().includes(q) ||
          m.email.toLowerCase().includes(q)
        ) {
          byId.set(m.member_id, m);
        }
      }
    }
    return [...byId.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [teams, q, existingMemberIds]);

  const toggleAdd = (memberID: string) =>
    setToAdd((prev) => {
      const next = new Set(prev);
      if (next.has(memberID)) next.delete(memberID);
      else next.add(memberID);
      return next;
    });

  // Save: rename and/or add — only the fields that actually changed. Errors
  // bubble through the existing error block.
  const saveMut = useMutation({
    mutationFn: async () => {
      if (!conversationId) return;
      const trimmed = nameDraft.trim();
      const renamed = (conversation?.title ?? '') !== trimmed;
      if (renamed) {
        await renameConversation(slug, conversationId, trimmed, token);
      }
      if (toAdd.size > 0) {
        await addParticipants(slug, conversationId, [...toAdd], token);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
      void queryClient.invalidateQueries({
        queryKey: ['chat', 'participants', slug, conversationId],
      });
      onClose();
    },
    onError: (err) => setErrorCode(err instanceof ApiError ? err.code : 'unknown'),
  });

  const removeMut = useMutation({
    mutationFn: (participantId: string) =>
      removeParticipant(slug, conversationId!, participantId, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['chat', 'participants', slug, conversationId],
      });
      void queryClient.invalidateQueries({ queryKey: ['chat', 'conversations', slug] });
    },
    onError: (err) => setErrorCode(err instanceof ApiError ? err.code : 'unknown'),
  });

  const onRemove = (participantId: string, label: string) =>
    void (async () => {
      const ok = await confirm({
        title: t('chat.removeMember.title'),
        body: t('chat.removeMember.body', { name: label }),
        confirmLabel: t('chat.removeMember.cta'),
        tone: 'destructive',
      });
      if (ok) removeMut.mutate(participantId);
    })();

  const trimmed = nameDraft.trim();
  const renamedChange = (conversation?.title ?? '') !== trimmed;
  const canSave = !saveMut.isPending && (renamedChange || toAdd.size > 0);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setErrorCode(null);
    saveMut.mutate();
  };

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <form onSubmit={onSubmit} noValidate className="flex w-[min(36rem,92vw)] flex-col gap-4 p-6">
        <header className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('chat.edit.eyebrow')}
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground">
              {t('chat.edit.title', { name: title })}
            </h2>
          </div>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('chat.edit.nameLabel')}
          <Input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            placeholder={t('chat.new.groupNamePlaceholder')}
            maxLength={120}
          />
        </label>

        <section className="flex flex-col gap-2">
          <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('chat.edit.currentMembers')}
          </h3>
          <div className="max-h-48 overflow-y-auto rounded-xl border border-border">
            {participantsQuery.isLoading && (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {t('chat.participants.loading')}
              </p>
            )}
            {participantsQuery.isError && (
              <p role="alert" className="px-3 py-2 text-sm text-destructive">
                {t('chat.participants.loadError')}
              </p>
            )}
            {!participantsQuery.isLoading && participants.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">
                {t('chat.participants.empty')}
              </p>
            )}
            {participants.map((p) => {
              const label = p.display_name || p.email || '—';
              // Don't render a remove button on the owner row.
              const isOwnerRow = !!p.user_id && p.user_id === conversation?.created_by_user_id;
              return (
                <div
                  key={p.id}
                  className="flex items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium text-foreground">{label}</span>
                    {!p.has_account && (
                      <span className="truncate text-[0.6875rem] text-muted-foreground">
                        {t('chat.participants.pending')}
                      </span>
                    )}
                  </span>
                  {!isOwnerRow && (
                    <button
                      type="button"
                      onClick={() => onRemove(p.id, label)}
                      aria-label={t('chat.removeMember.cta')}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash className="h-4 w-4" weight="bold" aria-hidden />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('chat.edit.addMembers')}
          </h3>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('chat.new.searchPlaceholder')}
            clearLabel={t('common.clearSearch')}
          />
          <div className="max-h-56 overflow-y-auto rounded-xl border border-border">
            {addressBookQuery.isLoading && (
              <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
            )}
            {addressBookQuery.isError && (
              <p role="alert" className="px-3 py-2 text-sm text-destructive">
                {t('chat.new.loadError')}
              </p>
            )}
            {!addressBookQuery.isLoading && pickerRows.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground">{t('chat.edit.noneToAdd')}</p>
            )}
            {pickerRows.map((m) => {
              const picked = toAdd.has(m.member_id);
              return (
                <button
                  key={m.member_id}
                  type="button"
                  onClick={() => toggleAdd(m.member_id)}
                  aria-pressed={picked}
                  className={cn(
                    'flex w-full min-h-11 items-center gap-3 border-b border-border px-3 py-2 text-left transition-colors last:border-b-0',
                    picked ? 'bg-primary/10' : 'hover:bg-muted/50',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
                      picked
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border',
                    )}
                  >
                    {picked && <Check className="h-3.5 w-3.5" weight="bold" />}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium text-foreground">
                      {m.display_name}
                    </span>
                    {m.email && (
                      <span className="truncate text-xs text-muted-foreground">{m.email}</span>
                    )}
                  </span>
                  {!m.has_account && (
                    <span className="shrink-0 rounded-pill bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {t('chat.new.notJoined')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`chat.edit.error.${errorCode}`, { defaultValue: t('chat.edit.error.unknown') })}
          </p>
        )}

        <footer className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saveMut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!canSave}>
            {saveMut.isPending ? t('chat.edit.saving') : t('chat.edit.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

